import {
  calculateTaxFromTotal,
  Money,
  TaxRate,
} from '@principle-theorem/accounting';
import { NameHelpers, Patient } from '@principle-theorem/principle-core';
import {
  IAppointment,
  IMohAgreementHolder,
  IMohClaimableItem,
  IMohOhsaClaimData,
  IMohOhsaClaimSummaryData,
  IMohOhsaIndividualTreatmentData,
  IMohPdfTreatment,
  IMohSdsClaimData,
  IMohSdsIndividualTreatmentData,
  IMohSdsSummaryData,
  IPatient,
  IStaffer,
  MohClaimType,
  MohSchoolException,
} from '@principle-theorem/principle-core/interfaces';
import { getEnumValues, toISODate, WithRef } from '@principle-theorem/shared';
import { groupBy, sum } from 'lodash';
import * as moment from 'moment-timezone';
import {
  IMohSendClaimDialogData,
  IMohSendClaimForm,
  SdsClaimForm,
} from '../../components/moh/moh-send-claim-dialog/moh-send-claim-dialog.component';
import { MohAgreementHolderForm } from '../moh-agreement-holder-form';
import { IMohCategorisedTreatmentData, MohHelpers } from '../moh-helpers';

interface IMohClaimTotals {
  totalIncGst: number;
  totalExGst: number;
  gstAmount: number;
}

export class MohClaimPdfDataBuilder {
  static getOhsaClaimData(
    data: IMohSendClaimDialogData,
    formData: IMohSendClaimForm,
    patient: WithRef<IPatient>,
    appointment?: WithRef<IAppointment>
  ): IMohOhsaClaimData | undefined {
    const treatments = MohHelpers.getCategorisedTreatmentData(
      data.claimItems,
      formData.claimType,
      formData.isAnnualConsultation
    );
    const treatmentData = this._getOhsaIndividualTreatmentData(
      formData,
      patient,
      treatments,
      appointment
    );
    const summaryData = this._getOhsaClaimSummaryData(
      MohAgreementHolderForm.toAgreementHolder(
        formData.agreementHolder,
        data.practice
      ),
      data.practitioner,
      treatments,
      data.claimUid
    );
    if (!treatmentData || !summaryData) {
      // eslint-disable-next-line no-console
      console.error('Failed to generate OHSA PDF data');
      return;
    }

    return {
      invoiceRef: data.invoice.ref,
      treatmentData,
      summaryData,
      claimUid: data.claimUid,
      approvalRequestRef: formData.approvalRequest?.ref,
    };
  }

  static getSdsClaimData(
    data: IMohSendClaimDialogData,
    patient: WithRef<IPatient>,
    sdsForm: SdsClaimForm,
    claimForm: IMohSendClaimForm,
    appointment?: WithRef<IAppointment>
  ): IMohSdsClaimData | undefined {
    const treatments = MohHelpers.getCategorisedTreatmentData(
      data.claimItems,
      MohClaimType.SDS,
      claimForm.isAnnualConsultation
    );
    const totals = this._getTotalPrices(treatments);
    const treatmentData = this._getSdsIndividualTreatmentData(
      patient,
      treatments,
      sdsForm,
      claimForm,
      totals,
      appointment
    );
    const summaryData = this._getSdsClaimSummaryData(
      MohAgreementHolderForm.toAgreementHolder(
        claimForm.agreementHolder,
        data.practice
      ),
      data.practitioner,
      totals,
      data.claimUid
    );
    if (!treatmentData || !summaryData) {
      // eslint-disable-next-line no-console
      console.error('Failed to generate SDS PDF data');
      return;
    }
    return {
      invoiceRef: data.invoice.ref,
      treatmentData,
      summaryData,
      claimUid: data.claimUid,
      approvalRequestRef: claimForm.approvalRequest?.ref,
    };
  }

  private static _getOhsaIndividualTreatmentData(
    formData: IMohSendClaimForm,
    patient: WithRef<IPatient>,
    treatments: IMohCategorisedTreatmentData,
    appointment?: WithRef<IAppointment>
  ): IMohOhsaIndividualTreatmentData | undefined {
    const standardCodes = treatments.standardPackage.map(
      (item) => item.serviceCode.code
    );
    return {
      patientNhiNumber:
        formData.nhiNumber ?? patient.nationalHealthIndexNumber ?? '',
      patientFirstName: NameHelpers.firstName(patient.name),
      patientLastName: NameHelpers.lastName(patient.name),
      patientDateOfBirth: patient.dateOfBirth ?? '',
      patientGender: patient.gender,
      schoolAttended: patient.metadata?.moh?.school?.schoolName ?? '',
      schoolNumber: this._getSchoolNumber(
        patient.metadata?.moh?.school?.schoolNumber
      ),
      schoolEqiCode: patient.metadata?.moh?.school?.eqiNumber?.toString() ?? '',
      annualConsultDate: appointment?.event?.from
        ? toISODate(appointment.event.from)
        : toISODate(moment()),
      annualConsultDone: formData.isAnnualConsultation,
      annualCapitatedFee: formData.isAnnualConsultation
        ? Patient.getMohCapitationBand(patient).fee
        : 0,
      standardCodes,
      additionalTreatments: this._toPdfTreatments(
        treatments.additionalTreatment,
        appointment
      ),
      preApprovalTreatments: this._toPdfTreatments(
        treatments.requiresApproval,
        appointment
      ),
      approvalNumber:
        formData.approvalRequest?.data.approvalNumber ??
        formData.approvalNumber ??
        '',
    };
  }

  private static _getOhsaClaimSummaryData(
    agreementHolder: IMohAgreementHolder,
    practitioner: WithRef<IStaffer>,
    claimItems: IMohCategorisedTreatmentData,
    claimUid: string
  ): IMohOhsaClaimSummaryData | undefined {
    const { totalExGst, gstAmount, totalIncGst } =
      this._getTotalPrices(claimItems);
    return {
      claimUid,
      payeeNumber: agreementHolder.payeeNumber,
      agreementNumber: agreementHolder.agreementNumber,
      agreementHolderName: agreementHolder.agreementHolderName,
      practitionerName: practitioner.user.name,
      dcnzNumber: practitioner.metadata?.nz?.providerDetails?.dcnzNumber ?? '',
      numberOfPatients: 1,
      totalExGst,
      gstAmount,
      totalIncGst,
      signatureDate: toISODate(moment()),
    };
  }

  private static _getSdsIndividualTreatmentData(
    patient: WithRef<IPatient>,
    treatments: IMohCategorisedTreatmentData,
    sdsForm: SdsClaimForm,
    claimForm: IMohSendClaimForm,
    totals: IMohClaimTotals,
    appointment?: WithRef<IAppointment>
  ): IMohSdsIndividualTreatmentData | undefined {
    return {
      patientNhiNumber:
        claimForm.nhiNumber ?? patient.nationalHealthIndexNumber ?? '',
      patientFirstName: NameHelpers.firstName(patient.name),
      patientLastName: NameHelpers.lastName(patient.name),
      patientDateOfBirth: patient.dateOfBirth ?? '',
      patientGender: patient.gender,
      patientAddress: patient.address ?? '',
      appointmentDate: appointment?.event?.from
        ? toISODate(appointment.event.from)
        : '',
      standardTreatments: this._toPdfTreatments(
        treatments.additionalTreatment,
        appointment
      ),
      preApprovalTreatments: this._toPdfTreatments(
        treatments.requiresApproval,
        appointment
      ),
      approvalNumber:
        claimForm.approvalRequest?.data.approvalNumber ??
        claimForm.approvalNumber ??
        '',
      totalExGst: totals.totalExGst,
      ...sdsForm,
    };
  }

  private static _getSdsClaimSummaryData(
    agreementHolder: IMohAgreementHolder,
    practitioner: WithRef<IStaffer>,
    totals: IMohClaimTotals,
    claimUid: string
  ): IMohSdsSummaryData | undefined {
    return {
      claimUid,
      payeeNumber: agreementHolder.payeeNumber,
      agreementNumber: agreementHolder.agreementNumber,
      agreementHolderName: agreementHolder.agreementHolderName,
      practitionerName: practitioner.user.name,
      dcnzNumber: practitioner.metadata?.nz?.providerDetails?.dcnzNumber ?? '',
      numberOfPatients: 1,
      totalExGst: totals.totalExGst,
      gstAmount: totals.gstAmount,
      totalIncGst: totals.totalIncGst,
      signatureDate: toISODate(moment()),
    };
  }

  private static _getTotalPrices(
    treatments: IMohCategorisedTreatmentData
  ): IMohClaimTotals {
    const allTreatments = [
      ...treatments.standardPackage,
      ...treatments.additionalTreatment,
      ...treatments.requiresApproval,
    ];
    const totalIncGst = Money.amount(
      sum(
        allTreatments.map(
          (treatment) => treatment.quantity * treatment.serviceCode.amount
        )
      )
    );
    const gstAmount = calculateTaxFromTotal(totalIncGst, TaxRate.GSTNZ);
    const totalExGst = Money.amount(totalIncGst - gstAmount);
    return {
      totalIncGst,
      gstAmount,
      totalExGst,
    };
  }

  private static _toPdfTreatments(
    claimItems: IMohClaimableItem[],
    appointment?: WithRef<IAppointment>
  ): IMohPdfTreatment[] {
    const groupedItems = groupBy(
      claimItems,
      (item) => `${item.serviceCode.code} - {item.serviceCode.amount}`
    );
    const date = appointment?.event?.from
      ? toISODate(appointment.event.from)
      : '';

    return Object.values(groupedItems).map((group) =>
      this._groupToPdfTreatment(group, date)
    );
  }

  private static _groupToPdfTreatment(
    group: IMohClaimableItem[],
    date: string
  ): IMohPdfTreatment {
    const baseItem = group[0];
    const quantity = sum(group.map((item) => item.quantity));
    const comments = group
      .map((item) => item.comments)
      .filter(Boolean)
      .join(', ');
    const teeth = group
      .map((item) => item.serviceCode.toothId)
      .filter(Boolean)
      .join(', ');

    return {
      code: baseItem.serviceCode.code,
      quantity,
      price: baseItem.serviceCode.amount,
      comments,
      teeth,
      date,
    };
  }

  private static _getSchoolNumber(schoolNumber?: number): string {
    if (
      !schoolNumber ||
      getEnumValues(MohSchoolException).includes(schoolNumber)
    ) {
      return '';
    }
    return schoolNumber.toString();
  }
}
